// hooks/useSaveToPatient.js
/**
 * Custom hook för att spara journal till patientjournal-modulen
 * Hanterar sparande och signering av PatientJournalEntry
 */
import { useState, useCallback } from 'react';
import apiClient from '../../../apiClient';
import { applyOrderFromTemplate } from '../utils/sectionHelpers';
import { toast } from 'react-toastify';

export const useSaveToPatient = (sections, selectedTemplate, sessionId) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [savedEntryId, setSavedEntryId] = useState(null);

  /**
   * Konvertera sections till journal content-format
   */
  const buildJournalContent = useCallback(() => {
    const orderedSecs = applyOrderFromTemplate(sections, selectedTemplate);
    return {
      sections: orderedSecs.map((sec, index) => ({
        title: sec.title,
        content: sec.content.trim(),
        order: index
      }))
    };
  }, [sections, selectedTemplate]);

  /**
   * Spara journal till patient (skapar PatientJournalEntry)
   * @param {number} patientId - Patient att spara till
   * @param {number} appointmentId - Valfri bokning att koppla till
   * @returns {Promise<number>} - entry_id för skapad journal
   */
  const saveToPatient = useCallback(async (patientId, appointmentId = null) => {
    if (!patientId) {
      toast.error('Ingen patient vald');
      return null;
    }

    if (sections.length === 0) {
      toast.error('Ingen journal att spara');
      return null;
    }

    setIsSaving(true);

    try {
      const content = buildJournalContent();

      const response = await apiClient.post('/api/journal/entries', {
        patient_id: patientId,
        template_ref: selectedTemplate?.template_id != null ? String(selectedTemplate.template_id) : null,
        appointment_id: appointmentId,
        notes_session_ref: sessionId ? String(sessionId) : null,
        content: content
      });

      const entryId = response.data.entry_id;
      setSavedEntryId(entryId);
      toast.success('Journal sparad till patientjournal!');
      return entryId;
    } catch (error) {
      console.error('Error saving to patient:', error);
      const errorMsg = error.response?.data?.error || 'Kunde inte spara journal';
      toast.error(errorMsg);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [sections, selectedTemplate, sessionId, buildJournalContent]);

  /**
   * Uppdatera befintlig journal
   * @param {number} entryId - Journal att uppdatera
   */
  const updateJournal = useCallback(async (entryId) => {
    if (!entryId) {
      toast.error('Ingen journal att uppdatera');
      return false;
    }

    setIsSaving(true);

    try {
      const content = buildJournalContent();

      await apiClient.put(`/api/journal/entries/${entryId}`, {
        content: content
      });

      toast.success('Journal uppdaterad!');
      return true;
    } catch (error) {
      console.error('Error updating journal:', error);
      const errorMsg = error.response?.data?.error || 'Kunde inte uppdatera journal';
      toast.error(errorMsg);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [buildJournalContent]);

  /**
   * Spara och signera journal
   * @param {number} patientId - Patient att spara till (om ny journal)
   * @param {number} entryId - Befintlig journal att signera (om redan sparad)
   * @param {number} appointmentId - Valfri bokning
   */
  const saveAndSign = useCallback(async (patientId, entryId = null, appointmentId = null) => {
    setIsSigning(true);

    try {
      let journalEntryId = entryId || savedEntryId;

      // Om ingen befintlig journal - skapa ny först
      if (!journalEntryId) {
        journalEntryId = await saveToPatient(patientId, appointmentId);
        if (!journalEntryId) {
          setIsSigning(false);
          return false;
        }
      } else {
        // Uppdatera befintlig journal innan signering
        const updated = await updateJournal(journalEntryId);
        if (!updated) {
          setIsSigning(false);
          return false;
        }
      }

      // Signera journalen
      await apiClient.post(`/api/journal/entries/${journalEntryId}/sign`);
      toast.success('Journal signerad!');
      return true;
    } catch (error) {
      console.error('Error signing journal:', error);
      const errorMsg = error.response?.data?.error || 'Kunde inte signera journal';
      toast.error(errorMsg);
      return false;
    } finally {
      setIsSigning(false);
    }
  }, [savedEntryId, saveToPatient, updateJournal]);

  return {
    saveToPatient,
    updateJournal,
    saveAndSign,
    isSaving,
    isSigning,
    savedEntryId,
    setSavedEntryId
  };
};
